import type { DealAccount } from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GetDealAccountsDocument } from '../../../graphql/query/deals/GetDealAccounts.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types.ts';

type UseDealAccountsFetchOptions = {
  skip?: boolean;
};

type UseDealAccountsFetchResult = {
  accounts: DealAccount[] | undefined;
  /**
   * Stable map from exchange account network ID (as selected in the Organization filter) to the
   * account UID expected by `/deal/landing` account filters. Used by the deals list to translate
   * Organization filter values before issuing a landing request.
   */
  accountUidByNetworkId: Map<string, string>;
  error: Error | null;
  loading: boolean;
  ready: boolean;
};

type RawAccount = {
  exchange_account_network_id?: string | null | number;
  account_uid?: string | null;
  name?: string | null;
};

const buildAccounts = (rawAccounts: RawAccount[] | undefined): DealAccount[] | undefined =>
  rawAccounts
    ?.filter(account => !!account.exchange_account_network_id)
    .map(account => ({
      id: String(account.exchange_account_network_id),
      name: account.name ?? '',
    }));

const buildUidLookup = (rawAccounts: RawAccount[] | undefined): Map<string, string> => {
  const map = new Map<string, string>();
  rawAccounts
    ?.filter(account => !!account.exchange_account_network_id && !!account.account_uid)
    .forEach(account => {
      map.set(String(account.exchange_account_network_id), String(account.account_uid));
    });
  return map;
};

export const useDealAccountsFetch = ({
  skip = false,
}: UseDealAccountsFetchOptions = {}): UseDealAccountsFetchResult => {
  const {
    data,
    error,
    isLoading: loading,
    isSuccess,
  } = useQuery({
    enabled: !skip,
    queryFn: ({ client }) => getGraphQLRequestFromClient(client)(GetDealAccountsDocument),
    queryKey: [QueryKeys.DEALS, 'accounts'],
    staleTime: Infinity,
  });

  const rawAccounts = data?.account;
  const accounts = useMemo(() => buildAccounts(rawAccounts), [rawAccounts]);
  const accountUidByNetworkId = useMemo(() => buildUidLookup(rawAccounts), [rawAccounts]);

  return {
    accounts,
    accountUidByNetworkId,
    error,
    loading,
    ready: skip || isSuccess,
  };
};
